// filepath: client/src/services/programs/programServices.js
import axios from 'axios';
import { SHARED_CONSTANTS } from '#shared/sharedConstants.js';
import { queryClient } from '#client/utils/queryClient.js';
import { getProgramQueryOptions } from '#client/utils/queryOptions.js';
import { isCustomer, buildAuthHeaders } from '#client/utils/helpers/services.js';
const API_URL = import.meta.env.VITE_API_URL;

// Dashboard services
export const programsDashboardService = {
    // **Paginated Programs** - For the programs list table
    async fetchPaginatedPrograms(firstPage = 1, limit = SHARED_CONSTANTS.MAX_PROGRAMS_PER_PAGE, filters = {}, sortBy = 'createdAt', sortDirection = 'desc') {
        const { isCustomerUser, customerId } = isCustomer();
        let customerParmas = {};
        if (isCustomerUser) {
            customerParmas.customerId = customerId;
        }

        const response = await axios.get(`${API_URL}/app/programs`, {
            params: {
                firstPage,
                limit,
                sortBy,
                sortDirection,
                ...filters, // status, customer, dateCreated, queryValue
                ...customerParmas,
            },
            headers: buildAuthHeaders(isCustomerUser),
        });
        return response.data?.data || { programs: [], total: 0, totalPages: 0, firstPage, limit };
    },

    // **Stats** - For the metrics cards
    async fetchProgramStats() {
        const { isCustomerUser, customerId } = isCustomer();
        let customerParmas = {};
        if (isCustomerUser) {
            customerParmas.customerId = customerId;
        }

        const response = await axios.get(`${API_URL}/app/programs/stats`, {
            params: {
                ...customerParmas,
            },
            headers: buildAuthHeaders(isCustomerUser),
        });
        return response.data?.data || { activePrograms: 0, totalVouchersCreated: 0, voucherRedemptionRate: 0 };
    },
};

// check if program name is unique
async function checkProgramName(name) {
    try {
        const res = await axios.get(`${API_URL}/app/programs/check-name`, {
            params: { name },
        });
        return res.data.isUnique;
    } catch (err) {
        console.error('[checkProgramName] failed:', err);
        // Fail gracefully: let validation continue
        return true;
    }
}

// higher-level validation
async function validateProgramName(name, mode, originalName) {
    const trimmed = name?.trim();
    const originalTrimmed = originalName?.trim();

    if (!trimmed) return null;

    if (mode === 'edit' && trimmed === originalTrimmed) {
        return null;
    }

    const isUnique = await checkProgramName(trimmed);

    return isUnique ? null : 'Program name already exists. Please choose a different name.';
}

export { checkProgramName, validateProgramName };

// Program services (staff + customer)
export const programService = {
    // **Fetch Program** - For the program detail page
    async fetchProgram(programId, mode) {
        const response = await queryClient.fetchQuery({
            ...getProgramQueryOptions(programId),
            queryFn: async () => {
                const { isCustomerUser, customerId } = isCustomer();
                let customerParmas = {};
                if (isCustomerUser) {
                    customerParmas.customerId = customerId;
                }

                const response = await axios.get(`${API_URL}/app/programs/${programId}`, {
                    params: {
                        mode: mode,
                        ...customerParmas,
                    },
                    headers: buildAuthHeaders(isCustomerUser),
                });
                return response;
            },
        });
        return response.data;
    },

    // **Update Program** - For editing program details
    async updateProgram(programId, programData) {
        const response = await axios.post(`${API_URL}/app/programs/${programId}/update`, programData, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Invaliate the program and programs list after update
        queryClient.invalidateQueries({ queryKey: ['programs', `program-${programId}`] });
        queryClient.invalidateQueries({ queryKey: ['program-dashboard'] });
        queryClient.invalidateQueries(['program-products', programId]);

        return response.data;
    },

    // **Create Program** - For adding a new program
    async createProgram(programData) {
        const response = await axios.post(`${API_URL}/app/programs`, programData, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Invalidate the programs list after creation
        queryClient.invalidateQueries({ queryKey: ['program-dashboard'] });

        return response.data;
    },

    // Archive Program (staff)
    async archiveProgram(programId) {
        const response = await axios.post(`${API_URL}/app/programs/archive/${programId}`, {}, {});

        // Invaliate the program and programs list after update
        queryClient.invalidateQueries({ queryKey: ['programs', `program-${programId}`] });
        queryClient.invalidateQueries({ queryKey: ['program-dashboard'] });

        return response.data;
    },

    // Products-only fetch for Program products table with pagination
    async fetchProgramProducts(programId, page = 1) {
        // CAC customer ID
        const isCustomerUser = await isCustomer();

        // Staff route
        const response = await axios.get(`${API_URL}/app/programs/${programId}/products`, {
            headers: buildAuthHeaders(isCustomerUser),
            params: { page, limit: 10 },
        });
        return response.data;
    },

    // Fetch all saved product/variant exclusions and price overrides for edit payload hydration
    async fetchProgramProductOverrides(programId) {
        const isCustomerUser = await isCustomer();
        const response = await axios.get(`${API_URL}/app/programs/${programId}/product-overrides`, {
            headers: buildAuthHeaders(isCustomerUser),
        });
        return response.data;
    },

    // Search and filter program products with pagination
    async searchProgramProducts(programId, { page = 1, search, minPrice, maxPrice, sortBy, sortDirection }) {
        const isCustomerUser = await isCustomer();
        const response = await axios.get(`${API_URL}/app/programs/${programId}/products/search`, {
            headers: buildAuthHeaders(isCustomerUser),
            params: {
                page,
                limit: 10,
                ...(search && { search }),
                ...(minPrice !== null && minPrice !== undefined && { minPrice }),
                ...(maxPrice !== null && maxPrice !== undefined && { maxPrice }),
                ...(sortBy && { sortBy }),
                ...(sortDirection && { sortDirection }),
            },
        });
        return response.data;
    },

    // Companies list (staff)
    async fetchCompaniesForProgram() {
        const response = await axios.get(`${API_URL}/api/companies`);
        return response.data;
    },

    // **Fetch Variants** - For the variants modal
    async fetchVariants(programId, productId) {
        const response = await axios.get(`${API_URL}/app/programs/${programId}/variants`, {
            params: { productId },
        });
        return response.data;
    },

    // **Update Variants** - For the variants modal
    async updateProgramVariants(programId, productId, variantUpdates) {
        const response = await axios.post(`${API_URL}/app/programs/${programId}/variants/update`, { productId, variantUpdates }, { headers: { 'Content-Type': 'application/json' } });
        return response.data;
    },

    async fetchProgramStats(programId) {
        const response = await axios.get(`${API_URL}/app/programs/${programId}/stats`);
        return response.data;
    },
};

// Voucher services (staff + customer)
export const voucherService = {
    // **Fetch Vouchers and Program** - For the voucher list page
    async fetchVouchersWithProgram(programId, isCustomer = false) {
        const response = await axios.get(`${API_URL}/api/app/vouchers/program/${programId}`, {
            headers: buildAuthHeaders(isCustomer),
        });
        return response.data.data;
    },

    // **Deactivate Voucher** - Deactivate a specific voucher
    async deactivateVoucher(voucherId, programId, isCustomer = false) {
        const response = await axios.patch(`${API_URL}/api/app/vouchers/${voucherId}/deactivate`, null, {
            headers: buildAuthHeaders(isCustomer),
        });

        // Invalidate vouchers queries after deactivation
        queryClient.invalidateQueries({ queryKey: ['vouchers', `program-${programId}`] });
        queryClient.invalidateQueries({ queryKey: ['programs', `program-${programId}`] });

        return response.data;
    },

    // **Extend Voucher Expiration** - Update a specific voucher's expiration date
    async extendVoucherExpiration(voucherId, programId, expirationDate, isCustomer = false) {
        const response = await axios.put(
            `${API_URL}/api/app/vouchers/${voucherId}/expiration`,
            { expirationDate },
            {
                headers: buildAuthHeaders(isCustomer),
            }
        );

        queryClient.invalidateQueries({ queryKey: ['vouchers', `program-${programId}`] });
        queryClient.invalidateQueries({ queryKey: ['programs', `program-${programId}`] });

        return response.data;
    },

    // **Deactivate All Vouchers** - Deactivate all vouchers in a program
    async deactivateAllVouchers(voucherIds = [], programId, isCustomer = false) {
        const response = await axios.patch(
            `${API_URL}/api/app/vouchers/deactivate-all`,
            { programId, voucherIds },
            {
                headers: buildAuthHeaders(isCustomer),
            }
        );
        // Invalidate vouchers queries after deactivation
        queryClient.invalidateQueries({ queryKey: ['vouchers', `program-${programId}`] });
        queryClient.invalidateQueries({ queryKey: ['programs', `program-${programId}`] });
        return response.data;
    },

    // **Resend Voucher Email** - Resend voucher email to recipient
    async resendVoucherEmail(voucherId, isCustomer = false) {
        const response = await axios.post(`${API_URL}/api/app/vouchers/${voucherId}/resend`, null, {
            headers: buildAuthHeaders(isCustomer),
        });
        return response.data;
    },

    // **Create Single Voucher** - Create a single voucher
    async createSingleVoucher(programId, payload, isCustomer = false) {
        const response = await axios.post(`${API_URL}/api/app/vouchers?programId=${programId}`, payload, {
            headers: buildAuthHeaders(isCustomer),
        });

        // Invalidate queries after creation
        queryClient.invalidateQueries({ queryKey: ['vouchers', `program-${programId}`] });
        queryClient.invalidateQueries({ queryKey: ['programs', `program-${programId}`] });

        return response.data;
    },

    // **Create Bulk Vouchers** - Create multiple vouchers at once
    async createBulkVouchers(programId, vouchers, isCustomer = false) {
        const response = await axios.post(
            `${API_URL}/api/app/vouchers/bulk`,
            { vouchers },
            {
                headers: buildAuthHeaders(isCustomer),
            }
        );

        // Invalidate queries after creation
        queryClient.invalidateQueries({ queryKey: ['vouchers', `program-${programId}`] });
        queryClient.invalidateQueries({ queryKey: ['programs', `program-${programId}`] });

        return response.data;
    },

    // **Check Active Vouchers** - Check if emails have active vouchers in a program
    async checkActiveVouchers(programId, emails, isCustomer = false) {
        const response = await axios.post(
            `${API_URL}/api/app/vouchers/bulk/check-active`,
            {
                programId,
                emails,
            },
            {
                headers: buildAuthHeaders(isCustomer),
            }
        );
        return response.data;
    },

    // **Bulk Delete Vouchers** - Permanently delete vouchers (used when PDF generation is cancelled)
    async bulkDeleteVouchers(programId, voucherIds, isCustomer = false) {
        const response = await axios.post(
            `${API_URL}/api/app/vouchers/bulk-delete`,
            {
                programId,
                voucherIds,
            },
            {
                headers: buildAuthHeaders(isCustomer),
            }
        );

        // Invalidate queries after deletion
        queryClient.invalidateQueries({ queryKey: ['vouchers', `program-${programId}`] });
        queryClient.invalidateQueries({ queryKey: ['programs', `program-${programId}`] });

        return response.data;
    },
};
